import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useUserEvent } from '../hooks';
import { usePlanLimits } from '../hooks/usePlanLimits';
import { ThemeContext } from '../App';
import { supabase } from '../supabaseClient';
import { loadVisibleAuthorizedProperties } from '../services/authorizedPropertiesService';
import { propertyActionAllowed } from '../utils/collaborationAccess';
import { loadPerformanceRecords } from '../services/performanceService';
import { agriculturalComparison, assignPerformanceCosts, buildPerformanceRows, currentYearPeriod, cycleLifecycle, cycleMetrics, hasValue, inPeriod, performanceDate, performanceKey as chaveDesempenho, previousComparableCycle, summarizePerformance, validatePeriod } from './performanceModel';
import UpgradeModal from '../components/UpgradeModal';
import DesempenhoPecuario from './DesempenhoPecuario';
import DesempenhoGeral from './DesempenhoGeral';
import PerformancePeriod from './PerformancePeriod';
import PerformanceComparison, { formatPerformanceValue } from './PerformanceComparison';
import { createAgricultureWorkbook, savePerformanceWorkbook } from './performanceReports';
import { carregarLogoRelatorio, criarCabecalhoRelatorio, criarDocumentoRelatorio, criarTabelaRelatorio } from '../utils/reportLayout';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import {
  FaArrowLeft, FaBalanceScale, FaBullseye, FaChartLine, FaCheck,
  FaCheckCircle, FaClipboardList, FaCoins, FaExclamationTriangle, FaLeaf,
  FaFileExcel, FaFilePdf, FaLock, FaMapMarkerAlt, FaPen, FaRulerCombined, FaSeedling,
  FaSpinner, FaWallet,
} from 'react-icons/fa';
import './Produtividade.css';

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts;

// Sugestões de cotação por cultura (R$/unidade) — apenas ponto de partida editável
const COTACOES_SUGERIDAS = {
  Soja: 130, Milho: 55, Feijão: 350, Trigo: 75, Arroz: 75,
  Café: 1400, 'Café Conilon': 1000, Algodão: 90,
  'Cana-de-açúcar': 100, Sorgo: 45, Girassol: 90, Amendoim: 160,
  Mandioca: 600, Eucalipto: 180,
};

// Produtividade típica nacional por cultura (unidade/ha)
const PROD_TIPICA = {
  Soja: 60, Milho: 150, Feijão: 30, Trigo: 50, Arroz: 70,
  Café: 30, 'Café Conilon': 40, Algodão: 200,
  'Cana-de-açúcar': 80, Sorgo: 80, Girassol: 40,
  Amendoim: 80, Mandioca: 200, Eucalipto: 35,
};

// Unidade de medida comercial por cultura
const UNIDADE_CULTURA = {
  Soja: 'sc', Milho: 'sc', Feijão: 'sc', Trigo: 'sc', Arroz: 'sc',
  Café: 'sc', 'Café Conilon': 'sc', Algodão: '@',
  'Cana-de-açúcar': 'ton', Sorgo: 'sc', Girassol: 'sc',
  Amendoim: 'sc', Mandioca: 'ton', Eucalipto: 'st',
};

function useIsMobile(bp = 768) {
  const [v, setV] = useState(() => typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const h = () => setV(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return v;
}

function MetricCard({ icon, label, value, sub, valueColor, darkMode }) {
  return (
    <div className="crop-performance-metric" role="group" aria-label={label} style={{
      background: darkMode ? '#1f2937' : '#f8fafc',
      border: `1px solid ${darkMode ? '#374151' : '#e2e8f0'}`,
      borderRadius: 12, padding: '12px 14px',
      display: 'flex', flexDirection: 'column', gap: 3,
    }}>
      <div style={{ fontSize: 11, color: darkMode ? '#9ca3af' : '#64748b', display: 'flex', alignItems: 'center', gap: 5 }}>
        <span className="crop-performance-metric-icon">{icon}</span> {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 800, color: valueColor || (darkMode ? '#f1f5f9' : '#0f172a') }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 10, color: darkMode ? '#6b7280' : '#94a3b8' }}>{sub}</div>}
    </div>
  );
}

export default function Produtividade({ onBack, user, onNavigate, pecuariaEnabled = false }) {
  useUserEvent({ userId: user?.id, tela: 'Produtividade', evento: 'page_view', comDuração: true });
  const { darkMode } = useContext(ThemeContext);
  const isMobile = useIsMobile();
  const { plano, isStarter: personalStarter, podeUsar, loading: planLoading } = usePlanLimits(user?.id);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const [propriedades, setPropriedades] = useState([]);
  const [propId, setPropId] = useState('');
  const currentProperty = propriedades.find(property => String(property.id) === String(propId));
  const sharedContext = Boolean(currentProperty?._collaborationPermissions);
  const performanceOwnerId = sharedContext ? currentProperty.user_id : user?.id;
  const isStarter = personalStarter && !sharedContext;
  const [talhoes, setTalhoes] = useState([]);
  const [ciclos, setCiclos] = useState([]);
  const [estimativas, setEstimativas] = useState({});
  const [lancamentos, setLancamentos] = useState([]);
  const [custosIndisponiveis, setCustosIndisponiveis] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingTalhoes, setLoadingTalhoes] = useState(false);
  const [falhaConsulta, setFalhaConsulta] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [gerandoRelatorio, setGerandoRelatorio] = useState(false);
  const [erro, setErro] = useState('');
  const [editandoId, setEditandoId] = useState(null);
  const editorRef = useRef(null);
  const [form, setForm] = useState({});
  const [filtroStatus, setFiltroStatus] = useState('andamento');
  const [filtroCiclo, setFiltroCiclo] = useState('todos');
  const [filtroTalhao, setFiltroTalhao] = useState('todos');
  const [periodo, setPeriodo] = useState(currentYearPeriod);
  const [periodoComparacao, setPeriodoComparacao] = useState(null);
  const [filtrarPlantio, setFiltrarPlantio] = useState(false);
  const [periodoPlantio, setPeriodoPlantio] = useState(currentYearPeriod);
  const [cicloComparacao, setCicloComparacao] = useState('');
  const [comparacaoAberta, setComparacaoAberta] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [visaoDesempenho, setVisaoDesempenho] = useState('geral');
  const canReadCosts = Boolean(currentProperty) && propertyActionAllowed(currentProperty, 'caderno', 'view', true);
  useEffect(() => {
    if (editandoId) editorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [editandoId]);
  useEffect(() => { if (visaoDesempenho === 'pecuaria' && !pecuariaEnabled) setVisaoDesempenho('agricultura'); }, [visaoDesempenho, pecuariaEnabled]);

  // ─── Carrega propriedades (próprias + compartilhadas) ───────────────────────
  useEffect(() => {
    let active = true;
    setPropriedades([]);
    setPropId('');
    if (!user?.id) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      setErro('');
      try {
        const { properties: lista } = await loadVisibleAuthorizedProperties(
          user.id,
          'id, nome, municipio, estado, user_id', 'produtividade'
        );
        if (!active) return;
        setPropriedades(lista);
        setPropId(previous => lista.some(property => String(property.id) === String(previous))
          ? previous : lista.length === 1 ? lista[0].id : '');
      } catch (error) {
        if (!active) return;
        console.error('Erro ao carregar propriedades de desempenho:', error);
        setErro('Não foi possível carregar as propriedades. Tente novamente em instantes.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [user?.id]);

  useEffect(() => {
    setFiltroCiclo('todos');
    setFiltroStatus('andamento');
    setFiltroTalhao('todos');
    setCicloComparacao('');
    setComparacaoAberta(false);
  }, [propId]);

  // ─── Carrega talhões + estimativas + custos ao selecionar propriedade ───────
  useEffect(() => {
    let active = true;
    setTalhoes([]);
    setCiclos([]);
    setEstimativas({});
    setLancamentos([]);
    setEditandoId(null);
    setFalhaConsulta(false);
    setCustosIndisponiveis(true);
    if (!propId || !currentProperty || !user?.id) {
      setLoadingTalhoes(false);
      if (propId && !currentProperty) {
        setFalhaConsulta(true);
        setErro('Seu acesso não permite consultar o desempenho agrícola e os gastos desta propriedade.');
      }
      return;
    }
    setLoadingTalhoes(true);
    setErro('');
    (async () => {
      try {
        const records = await loadPerformanceRecords(propId, performanceOwnerId, canReadCosts);
        if (!active) return;
        setTalhoes(records.plots);
        setCiclos(records.cycles);
        setLancamentos(records.entries);
        setCustosIndisponiveis(Boolean(records.costsError));
        if (records.costsError) {
          console.error('Erro ao carregar gastos de desempenho:', records.costsError);
          setErro(`Gastos indisponíveis. ${records.costsError.message || 'Atualize antes de considerar os totais.'}`);
        }
        const estMap = {};
        records.estimates.forEach(e => {
          estMap[chaveDesempenho(e.talhao_id, e.ciclo_cultivo_id)] = {
            id: e.id, cicloId: e.ciclo_cultivo_id || null,
            produtividade: e.produtividade_estimada?.toString() ?? '',
            cotacao: e.cotacao_unitaria?.toString() ?? '',
            real: e.produtividade_real?.toString() ?? '',
            unidade: e.unidade || 'sc',
          };
        });
        setEstimativas(estMap);
      } catch (error) {
        if (!active) return;
        setFalhaConsulta(true);
        console.error('Erro ao carregar desempenho:', error);
        setErro('Não foi possível carregar o desempenho completo. Tente atualizar a consulta.');
      } finally {
        if (active) setLoadingTalhoes(false);
      }
    })();
    return () => { active = false; };
  }, [propId, currentProperty, user?.id, performanceOwnerId, canReadCosts, refreshKey]);

  function abrirEdicao(talhao, informarResultado = false) {
    if (planLoading || isStarter) { setShowUpgrade(true); return; }
    if (talhao._readOnly) { setErro('Este plantio físico ainda não possui vínculo para gravar metas de produtividade. Os dados permanecem somente para consulta.'); return; }
    const est = estimativas[talhao._performanceKey] || {};
    if (!propertyActionAllowed(currentProperty, 'produtividade', est.id ? 'edit' : 'create', true)) { setErro('Seu acesso permite consultar, mas não alterar esta meta.'); return; }
    const unidPadrao = UNIDADE_CULTURA[talhao.cultura] || 'sc';
    setForm({
      produtividade: est.id ? est.produtividade ?? '' : informarResultado ? '' : PROD_TIPICA[talhao.cultura]?.toString() || '',
      cotacao: est.id ? est.cotacao ?? '' : informarResultado ? '' : COTACOES_SUGERIDAS[talhao.cultura]?.toString() || '',
      real: est.real || '',
      unidade: est.unidade || unidPadrao,
    });
    setEditandoId(talhao._performanceKey);
  }

  async function salvar(talhao) {
    if (salvando) return;
    if (planLoading || isStarter) { setShowUpgrade(true); return; }
    if (talhao._readOnly) { setErro('Este plantio ainda não permite gravar metas.'); return; }
    if (['produtividade', 'cotacao', 'real'].some(key => form[key] !== '' && (!hasValue(form[key]) || Number(form[key]) < 0))) {
      setErro('Informe valores numéricos maiores ou iguais a zero.');
      return;
    }
    const existing = estimativas[talhao._performanceKey];
    if (!propertyActionAllowed(currentProperty, 'produtividade', existing?.id ? 'edit' : 'create', true)) { setErro('Alteração não autorizada nesta propriedade.'); return; }
    setSalvando(true);
    const payload = {
      talhao_id: talhao.id,
      ciclo_cultivo_id: talhao._ciclo?.id || null,
      user_id: performanceOwnerId,
      produtividade_estimada: form.produtividade ? parseFloat(form.produtividade) : null,
      cotacao_unitaria: form.cotacao ? parseFloat(form.cotacao) : null,
      produtividade_real: form.real ? parseFloat(form.real) : null,
      unidade: form.unidade || 'sc',
    };

    setErro('');
    try {
      if (existing?.id) {
        const { data, error } = await supabase.from('produtividade_talhao').update(payload).eq('id', existing.id).select('id').single();
        if (error) throw error;
        if (!data?.id) throw new Error('Atualização da meta não confirmada pelo banco.');
        setEstimativas(prev => ({ ...prev, [talhao._performanceKey]: { ...prev[talhao._performanceKey], ...form } }));
      } else {
        const { data, error } = await supabase.from('produtividade_talhao').insert(payload).select().single();
        if (error) throw error;
        if (!data?.id) throw new Error('Gravação da meta não confirmada pelo banco.');
        setEstimativas(prev => ({ ...prev, [talhao._performanceKey]: { ...form, id: data.id, cicloId: talhao._ciclo?.id || null } }));
      }
      setEditandoId(null);
    } catch (error) {
      console.error('Erro ao salvar meta de desempenho:', error);
      setErro('Não foi possível salvar a meta deste talhão. Nenhuma alteração foi aplicada.');
    } finally {
      setSalvando(false);
    }
  }

  const todosCiclos = useMemo(() => buildPerformanceRows(talhoes, ciclos), [talhoes, ciclos]);
  const conciliacao = useMemo(() => assignPerformanceCosts(lancamentos, todosCiclos, talhoes), [lancamentos, todosCiclos, talhoes]);
  const custos = conciliacao.costs;
  const ciclosHistoricos = useMemo(() => filtrarPlantio ? todosCiclos.filter(row => inPeriod(row.data_plantio, periodoPlantio)) : todosCiclos,
    [todosCiclos, filtrarPlantio, periodoPlantio]);
  const ciclosSelecionados = useMemo(() => ciclosHistoricos.filter(row =>
    (filtroCiclo === 'todos' || row._performanceKey === filtroCiclo) && (filtroTalhao === 'todos' || row._plotKey === filtroTalhao)),
  [ciclosHistoricos, filtroCiclo, filtroTalhao]);
  const talhoesFiltrados = useMemo(() => ciclosSelecionados.filter(row => {
    if (filtroStatus === 'andamento') return cycleLifecycle(row) !== 'harvested';
    if (filtroStatus === 'colhidos') return cycleLifecycle(row) === 'harvested';
    return true;
  }), [ciclosSelecionados, filtroStatus]);
  const resumo = useMemo(() => summarizePerformance(talhoesFiltrados, estimativas, custos), [talhoesFiltrados, estimativas, custos]);
  const referencia = todosCiclos.find(row => row._performanceKey === cicloComparacao);
  const metricasComparacao = referencia ? agriculturalComparison(resumo, summarizePerformance([referencia], estimativas, custos), custosIndisponiveis) : [];
  const cicloLabel = row => `${row.cultura} · plantio ${row.data_plantio.split('-').reverse().join('/')} · ${row.nome}`;
  const talhoesDisponiveis = [...new Map(ciclosHistoricos.map(row => [row._plotKey, row.nome])).entries()];
  const cicloAnterior = talhoesFiltrados.length === 1 ? previousComparableCycle(talhoesFiltrados[0], todosCiclos) : null;
  const faltaResultado = row => cycleLifecycle(row) === 'harvested' && !hasValue(estimativas[row._performanceKey]?.real);
  const pendencias = talhoesFiltrados.filter(row => faltaResultado(row) || !(Number(estimativas[row._performanceKey]?.produtividade) > 0));
  const selecaoLabel = talhoesFiltrados.length === 1 ? cicloLabel(talhoesFiltrados[0])
    : `${talhoesFiltrados.length} ciclos selecionados${filtrarPlantio ? ` · plantios de ${periodoPlantio.start} a ${periodoPlantio.end}` : ''}`;
  const filtroHistoricoValido = !filtrarPlantio || !validatePeriod(periodoPlantio);
  useEffect(() => {
    if (!loadingTalhoes && filtroCiclo !== 'todos' && !ciclosHistoricos.some(row => row._performanceKey === filtroCiclo)) setFiltroCiclo('todos');
    if (!loadingTalhoes && filtroTalhao !== 'todos' && !ciclosHistoricos.some(row => row._plotKey === filtroTalhao)) setFiltroTalhao('todos');
  }, [loadingTalhoes, ciclosHistoricos, filtroCiclo, filtroTalhao]);

  function conferirGastos() {
    const dates = conciliacao.assignments.map(entry => performanceDate(entry.date))
      .filter(date => date && !validatePeriod({ start: date, end: date })).sort();
    if (!dates.length) { setErro('Os gastos não possuem datas válidas para consultar o histórico. Confira os lançamentos de origem.'); return; }
    setPeriodo({ start: dates[0], end: dates[dates.length - 1] });
    setVisaoDesempenho('geral');
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────
  const fmtBRL = v => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
  const fmtNum = (v, dec = 1) => Number(v).toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
  const fmtProducao = producaoPorUnidade => Object.entries(producaoPorUnidade)
    .map(([unidade, valor]) => `${fmtNum(valor, 0)} ${unidade}`)
    .join(' · ');
  const lifecycle = cycleLifecycle;
  const isColhido = t => lifecycle(t) === 'harvested';
  const isPlanejado = t => lifecycle(t) === 'planned';

  const produtividades = Object.keys({ ...resumo.producaoPorUnidade, ...resumo.producaoReal }).map(key => {
    const real = resumo.areaComResultado[key] > 0;
    const area = real ? resumo.areaComResultado[key] : resumo.areaComMetaPorUnidade[key];
    const production = real ? resumo.producaoReal[key] : resumo.producaoPorUnidade[key];
    return area > 0 ? `${key}: ${fmtNum(production / area)}/ha · ${real ? 'real' : 'meta'}` : `${key}: área não informada`;
  });

  // ─── Filtro por status (em desenvolvimento / colhidos) ───────────────────────
  const contagemStatus = useMemo(() => {
    let desenvolvimento = 0, planejados = 0, colhidos = 0;
    ciclosSelecionados.forEach(t => {
      if (cycleLifecycle(t) === 'harvested') colhidos++;
      else if (cycleLifecycle(t) === 'planned') planejados++;
      else desenvolvimento++;
    });
    return { desenvolvimento, planejados, colhidos };
  }, [ciclosSelecionados]);
  function autorizarExportacao(format) {
    if (planLoading) return false;
    if (format === 'pdf' && !sharedContext && !podeUsar('relatorios_pdf')) {
      setShowUpgrade(true);
      return false;
    }
    return true;
  }

  async function exportarRelatorio(format = 'pdf') {
    if (gerandoRelatorio || !autorizarExportacao(format)) return;
    if (custosIndisponiveis) {
      setErro('Os gastos estão indisponíveis. Atualize a página antes de exportar os totais.');
      return;
    }
    if (!filtroHistoricoValido || talhoesFiltrados.length === 0) {
      setErro('Não há talhões nos filtros selecionados para gerar o relatório.');
      return;
    }

    setGerandoRelatorio(true);
    setErro('');
    try {
      const statusLabel = {
        todos: 'Todos os estágios',
        andamento: 'Em andamento (inclui planejados)',
        colhidos: 'Concluídas',
      }[filtroStatus];
      const filterLabel = `${statusLabel} · ${selecaoLabel} · ${filtroTalhao !== 'todos' ? `Talhão: ${talhoesDisponiveis.find(([id]) => id === filtroTalhao)?.[1]} · ` : ''}${filtrarPlantio ? `Plantios de ${periodoPlantio.start} a ${periodoPlantio.end}` : 'Todos os anos de plantio'}`;
      const nomeSeguro = String(propAtual?.nome || 'propriedade').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '_').toLowerCase();
      if (format === 'xlsx') {
        await savePerformanceWorkbook(createAgricultureWorkbook({
          rows: talhoesFiltrados, estimates: estimativas, costs: custos, context: propAtual?.nome,
          filterLabel,
          comparison: referencia ? {
            metrics: metricasComparacao, currentLabel: selecaoLabel,
            previousLabel: cicloLabel(referencia),
          } : null,
        }), `desempenho_ciclos_${nomeSeguro}.xlsx`);
        return;
      }
      const logoBase64 = await carregarLogoRelatorio();
      const geradoEm = new Date();

      const resumoFiltrado = resumo;
      const body = talhoesFiltrados.map(talhao => {
        const est = estimativas[talhao._performanceKey];
        const { area, cost: custo, revenue: receita, margin: margem } = cycleMetrics(talhao, est, custos);
        const unidade = est?.unidade || UNIDADE_CULTURA[talhao.cultura] || 'sc';
        const cell = value => String(value ?? '-');
        return [
          cell(`${talhao.nome}\n${talhao.data_plantio} · ${talhao.data_colheita_efetiva || 'em aberto'}`),
          cell(talhao.cultura || '-'),
          cell(`${fmtNum(area)} ha`),
          cell(est?.produtividade ? `${fmtNum(est.produtividade)} ${unidade}/ha` : '-'),
          cell(hasValue(est?.real) ? `${fmtNum(est.real)} ${unidade}/ha` : '-'),
          cell(custo != null ? fmtBRL(custo) : 'Sem registros'),
          cell(receita != null ? fmtBRL(receita) : '-'),
          cell(margem != null ? fmtBRL(margem) : '-'),
        ];
      });
      const cycleTable = criarTabelaRelatorio([
        ['TALHÃO', 'CULTURA', 'ÁREA', 'META', 'REAL', 'CUSTO', 'RECEITA PROJ.', 'SALDO PROJ.'],
        ...body,
      ], true);
      cycleTable.table.widths = ['*', 65, 48, 68, 68, 64, 68, 68];
      cycleTable.fontSize = 7;
      const docDefinition = {
        ...criarDocumentoRelatorio([], geradoEm),
        pageOrientation: 'landscape',
        content: [
          criarCabecalhoRelatorio('Relatório de Desempenho dos Ciclos Agrícolas', {
            propriedade: propAtual?.nome || 'Propriedade', area: `${talhoesFiltrados.length} ciclos selecionados`,
          }, logoBase64, geradoEm),
          { text: `Filtro: ${filterLabel}`, fontSize: 8, margin: [0, 0, 0, 3] },
          { text: filtrarPlantio ? `Plantios de ${periodoPlantio.start} a ${periodoPlantio.end}` : 'Todos os anos de plantio', fontSize: 8, margin: [0, 0, 0, 8] },
          {
            table: {
              widths: ['*', '*', '*', '*'],
              body: [[
                { text: `ÁREA FÍSICA\n${fmtNum(resumoFiltrado.area)} ha`, style: 'metric' },
                { text: `ÁREA CULTIVADA ACUMULADA\n${fmtNum(resumoFiltrado.areaCultivada)} ha`, style: 'metric' },
                { text: `CUSTOS\n${resumoFiltrado.comCustos ? fmtBRL(resumoFiltrado.custo) : 'Sem registros'}`, style: 'metric' },
                { text: `SALDO PARCIAL PROJETADO\n${resumoFiltrado.receita && resumoFiltrado.margem != null ? fmtBRL(resumoFiltrado.margem) : 'Dados insuficientes'}`, style: 'metric' },
              ]],
            },
            layout: { hLineColor: () => '#cfe4df', vLineColor: () => '#cfe4df', paddingTop: () => 8, paddingBottom: () => 8 },
            margin: [0, 0, 0, 12],
          },
          cycleTable,
          ...(referencia ? [
            { text: `COMPARAÇÃO COM ${cicloLabel(referencia)}`, fontSize: 10, bold: true, margin: [0, 12, 0, 6] },
            { ...criarTabelaRelatorio([
              ['INDICADOR', 'SELECIONADO', 'REFERÊNCIA'],
              ...metricasComparacao.map(metric => [metric.label, formatPerformanceValue(metric.current, metric.currency), formatPerformanceValue(metric.previous, metric.currency)]),
            ], true), fontSize: 8 },
          ] : []),
          { text: 'Custos associados por correspondência única de data e talhão, sem rateio. Gastos gerais e sem ciclo compatível estão na visão Geral. Ausência de lançamentos não comprova custo zero. Projeções não representam vendas nem lucro realizado.', fontSize: 8, margin: [0, 12, 0, 0] },
          { text: 'Histórico consultado nos registros atuais, com área e metas atuais. Não é um fechamento imutável; alterações posteriores podem mudar os resultados. Custo por hectare exige gastos e área em todos os ciclos selecionados. Custo por unidade usa ciclos com resultado informado, exigindo área e gastos em todos eles, por cultura/unidade.', fontSize: 8, margin: [0, 6, 0, 0] },
        ],
        styles: {
          metric: { alignment: 'center', color: '#1976d2', bold: true, fontSize: 8 },
        },
      };
      pdfMake.createPdf(docDefinition).download(`desempenho_ciclos_${nomeSeguro}.pdf`);
    } catch (error) {
      console.error('Erro ao gerar relatório de desempenho:', error);
      setErro('Não foi possível gerar o relatório. Tente novamente.');
    } finally {
      setGerandoRelatorio(false);
    }
  }

  // ─── Estilos base ────────────────────────────────────────────────────────────
  const padL = `calc(env(safe-area-inset-left, 0px) + ${isMobile ? '14px' : '24px'})`;
  const padR = `calc(env(safe-area-inset-right, 0px) + ${isMobile ? '14px' : '24px'})`;  // right side uses its own inset
  const bg = darkMode ? '#111827' : '#f0f4f8';
  const cardBg = darkMode ? '#1a2234' : '#ffffff';
  const border = darkMode ? '#1f2937' : '#e8edf3';
  const textPrimary = darkMode ? '#f1f5f9' : '#0f172a';
  const textSecondary = darkMode ? '#9ca3af' : '#64748b';
  const inputStyle = {
    width: '100%', padding: isMobile ? '12px' : '9px 12px', borderRadius: 8,
    fontSize: 16,  // ≥16px prevents iOS auto-zoom on focus
    border: `1.5px solid ${darkMode ? '#374151' : '#cbd5e1'}`,
    background: darkMode ? '#0f172a' : '#fff',
    color: textPrimary, outline: 'none', boxSizing: 'border-box',
    WebkitAppearance: 'none',
  };

  const propAtual = currentProperty;

  return (
    <div className={`crop-performance-shell ${darkMode ? 'is-dark' : ''}`} style={{
      minHeight: '100vh', background: bg,
      paddingTop: `calc(env(safe-area-inset-top, 0px) + ${isMobile ? '12px' : '24px'})`,
      paddingLeft: padL, paddingRight: padR,
      paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + 24px)`,
    }}>

      {/* ── Header sticky ────────────────────────────────────────────────────── */}
      <div className="crop-performance-topbar" style={{
        position: 'sticky', top: 0, zIndex: 20,
        background: darkMode ? '#111827' : '#fff',
        marginLeft: `calc(-1 * (env(safe-area-inset-left, 0px) + ${isMobile ? '14px' : '24px'}))`,
        marginRight: `calc(-1 * (env(safe-area-inset-right, 0px) + ${isMobile ? '14px' : '24px'}))`,
        paddingLeft: padL, paddingRight: padR,
        paddingTop: isMobile ? 10 : 14, paddingBottom: isMobile ? 10 : 14,
        borderBottom: `1px solid ${border}`,
        boxShadow: '0 2px 12px rgba(0,0,0,0.07)', marginBottom: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14 }}>
          {/* Botão voltar */}
          <button className="crop-performance-back" aria-label="Voltar" onClick={onBack} style={{
            flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
            padding: isMobile ? '10px 4px' : '6px 4px', minHeight: 44,
            color: darkMode ? '#60a5fa' : '#0284c7',
            fontSize: isMobile ? 13 : 14, fontWeight: 600, borderRadius: 6,
            WebkitTapHighlightColor: 'transparent',
          }}>
            <FaArrowLeft />
            {!isMobile && <span>Voltar</span>}
          </button>

          <div style={{ width: 1, height: isMobile ? 28 : 32, background: border, flexShrink: 0 }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12, minWidth: 0 }}>
            <div className="crop-performance-brand-icon" style={{
              width: isMobile ? 34 : 40, height: isMobile ? 34 : 40,
              borderRadius: 11, flexShrink: 0,
              background: 'linear-gradient(135deg, #43a047, #2e7d32)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: isMobile ? 16 : 20,
              boxShadow: '0 3px 10px rgba(67,160,71,0.35)',
            }}><FaChartLine /></div>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ fontSize: isMobile ? 16 : 20, fontWeight: 700, color: textPrimary, margin: 0 }}>
                Desempenho da Propriedade
              </h1>
              <div style={{ fontSize: isMobile ? 11 : 12, color: textSecondary, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {propAtual ? `${propAtual.nome} · ${propAtual.municipio}/${propAtual.estado}` : 'Metas, resultado e viabilidade por talhão'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {(
        <nav className="crop-performance-filters crop-performance-mode-tabs" aria-label="Tipo de desempenho" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          {[{ id: 'geral', label: 'Geral' }, { id: 'agricultura', label: 'Agricultura' }, ...(pecuariaEnabled ? [{ id: 'pecuaria', label: 'Pecuária' }] : [])].map(op => {
            const ativo = visaoDesempenho === op.id;
            return (
              <button
                key={op.id}
                type="button"
                disabled={salvando}
                aria-pressed={ativo}
                onClick={() => setVisaoDesempenho(op.id)}
                style={{
                  minHeight: 38, padding: '0 18px', borderRadius: 20,
                  fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  background: ativo ? 'linear-gradient(135deg, #43a047, #2e7d32)' : (darkMode ? '#1f2937' : '#f1f5f9'),
                  color: ativo ? '#fff' : textSecondary,
                  border: `1.5px solid ${ativo ? 'transparent' : border}`,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                {op.label}
              </button>
            );
          })}
        </nav>
      )}

      {visaoDesempenho === 'pecuaria' ? (
        <DesempenhoPecuario propertyId={propId} onPropertyChange={setPropId} darkMode={darkMode} onNavigate={onNavigate} period={periodo} onPeriodChange={setPeriodo} comparisonPeriod={periodoComparacao} onComparisonChange={setPeriodoComparacao} disabled={salvando} onDataChanged={() => setRefreshKey(value => value + 1)} onBeforeExport={autorizarExportacao} exportDisabled={planLoading} />
      ) : <>

      {/* ── Seletor de propriedade ─────────────────────────────────────────── */}
      {!loading && propriedades.length > 1 && (
        <label className="crop-performance-property-select">
          <span><FaMapMarkerAlt /> Área analisada</span>
          <select
            aria-label="Propriedade do desempenho"
            value={propId}
            disabled={salvando}
            onChange={e => { setPropId(e.target.value); setEditandoId(null); }}
          >
            <option value="">Selecione uma propriedade…</option>
            {propriedades.map(p => <option key={p.id} value={p.id}>{p.nome} — {p.municipio}/{p.estado}</option>)}
          </select>
        </label>
      )}

      {!loading && propId && (
        <div className="crop-performance-actions" role="toolbar" aria-label="Ações do desempenho">
          <button type="button" className="crop-performance-report-action" disabled={loadingTalhoes || salvando} onClick={() => setRefreshKey(value => value + 1)}>Atualizar dados</button>
        </div>
      )}
      {erro && <div className="crop-performance-error" role="alert"><FaExclamationTriangle /><span>{erro}</span></div>}

      {/* ── Estados vazios ────────────────────────────────────────────────── */}
      {loading && (
        <div className="crop-performance-empty" style={{ textAlign: 'center', padding: '60px 20px', color: textSecondary }}>
          <FaSpinner className="crop-performance-spinner" />
          <strong>Carregando propriedades</strong>
          <span>Organizando os indicadores disponíveis.</span>
        </div>
      )}

      {!loading && !propId && (
        <div className="crop-performance-empty" style={{ textAlign: 'center', padding: '60px 20px', color: textSecondary }}>
          <FaLeaf />
          <strong>Selecione uma propriedade</strong>
          <span>Escolha a área acima para analisar gastos por período e resultados dos ciclos produtivos.</span>
        </div>
      )}

      {!loading && propId && loadingTalhoes && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: textSecondary }}>Carregando talhões…</div>
      )}

      {!loading && propId && !loadingTalhoes && !falhaConsulta && visaoDesempenho === 'geral' && (
        <DesempenhoGeral propertyId={propId} propertyName={propAtual?.nome} assignments={conciliacao.assignments} costsUnavailable={custosIndisponiveis} period={periodo} onPeriodChange={setPeriodo} pecuariaEnabled={pecuariaEnabled && propertyActionAllowed(currentProperty, 'pecuaria', 'view', true)} refreshKey={refreshKey} onNavigate={onNavigate} comparisonPeriod={periodoComparacao} onComparisonChange={setPeriodoComparacao} onBeforeExport={autorizarExportacao} exportDisabled={planLoading} />
      )}
      {!loading && propId && !loadingTalhoes && !falhaConsulta && visaoDesempenho === 'agricultura' && todosCiclos.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: textSecondary }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🌱</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: textPrimary }}>Nenhum ciclo agrícola disponível</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Cadastre um plantio em Propriedades e Talhões. Gastos sem ciclo permanecem na visão Geral.</div>
        </div>
      )}

      {!loading && propId && !loadingTalhoes && !falhaConsulta && visaoDesempenho === 'agricultura' && todosCiclos.length > 0 && (
        <>
          <nav className="crop-performance-filters performance-stage-tabs" aria-label="Situação da safra">
            {[
              { id: 'andamento', label: `Em andamento (${contagemStatus.desenvolvimento + contagemStatus.planejados})` },
              { id: 'colhidos', label: `Concluídas (${contagemStatus.colhidos})` },
              { id: 'todos', label: `Todas (${ciclosSelecionados.length})` },
            ].map(option => <button type="button" key={option.id} aria-pressed={filtroStatus === option.id}
              className={filtroStatus === option.id ? 'is-active' : ''} disabled={salvando}
              onClick={() => { setFiltroStatus(option.id); setEditandoId(null); }}>{option.label}</button>)}
          </nav>
          <div className="performance-selection">
            <label className="crop-performance-property-select">
              <span><FaSeedling /> Safra / ciclo</span>
              <select aria-label="Ciclo analisado" value={filtroCiclo} disabled={salvando} onChange={event => {
                setFiltroCiclo(event.target.value); setFiltroStatus('todos'); setFiltroTalhao('todos'); setEditandoId(null); setCicloComparacao('');
              }}>
                <option value="todos">Todos os ciclos</option>
                {ciclosHistoricos.map(row => <option key={row._performanceKey} value={row._performanceKey}>{cicloLabel(row)}</option>)}
              </select>
            </label>
            <label className="crop-performance-property-select">
              <span><FaMapMarkerAlt /> Talhão</span>
              <select aria-label="Talhão analisado" value={filtroTalhao} disabled={salvando || filtroCiclo !== 'todos'} onChange={event => {
                setFiltroTalhao(event.target.value); setEditandoId(null); setCicloComparacao('');
              }}>
                <option value="todos">{filtroCiclo !== 'todos' ? 'Talhão do ciclo selecionado' : 'Todos os talhões'}</option>
                {talhoesDisponiveis.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
            </label>
          </div>
          <details className="performance-disclosure">
            <summary>Filtrar histórico por período</summary>
          <label className="performance-toggle"><input type="checkbox" checked={filtrarPlantio} disabled={salvando} onChange={event => { setFiltrarPlantio(event.target.checked); setFiltroCiclo('todos'); setFiltroStatus('todos'); setEditandoId(null); }} />Consultar histórico por data de plantio</label>
          {filtrarPlantio && <fieldset className="performance-history-fields" disabled={salvando}><PerformancePeriod period={periodoPlantio} onChange={value => { setPeriodoPlantio(value); setFiltroCiclo('todos'); setFiltroStatus('todos'); setEditandoId(null); }} dates={todosCiclos.map(row => row.data_plantio)} /></fieldset>}
            <p className="performance-notice">Selecione o período de plantio. Os gastos continuam abrangendo o ciclo inteiro, mesmo quando a safra atravessa anos.</p>
          </details>
          {filtrarPlantio && <p className="performance-notice" role="status">Filtro ativo: plantios de {periodoPlantio.start} a {periodoPlantio.end}. <button className="performance-text-action" disabled={salvando} onClick={() => setFiltrarPlantio(false)}>Limpar período</button></p>}
          <details className="performance-disclosure" open={comparacaoAberta} onToggle={event => setComparacaoAberta(event.currentTarget.open)}>
            <summary>Comparar com outra safra</summary>
            <button type="button" className="crop-performance-report-action" disabled={!cicloAnterior || salvando}
              onClick={() => setCicloComparacao(cicloAnterior._performanceKey)}>Comparar com ciclo anterior</button>
            <p className="performance-notice">{cicloAnterior ? `Sugestão: ${cicloLabel(cicloAnterior)}. Mesmo talhão e cultura; você pode trocar a referência abaixo.`
              : 'Selecione um único ciclo para sugerir o último plantio concluído da mesma cultura e talhão, ou escolha uma referência abaixo.'}</p>
          <label className="crop-performance-property-select"><span>Comparar com ciclo</span><select aria-label="Comparar com ciclo" value={referencia ? cicloComparacao : ''} disabled={salvando} onChange={event => setCicloComparacao(event.target.value)}>
            <option value="">Sem comparação</option>{todosCiclos.map(row => <option key={row._performanceKey} value={row._performanceKey}>{cicloLabel(row)}</option>)}
          </select></label>
          {referencia && filtroHistoricoValido && talhoesFiltrados.length > 0 && <PerformanceComparison onBeforeExport={autorizarExportacao} exportDisabled={planLoading} metrics={metricasComparacao} context={propAtual?.nome}
            currentLabel={selecaoLabel} previousLabel={cicloLabel(referencia)} />}
            <p className="performance-notice">Custos por unidade usam somente ciclos com produção real informada, sem misturar culturas ou unidades. Falta de gastos nesses ciclos impede o cálculo.</p>
          </details>

          {filtroHistoricoValido && <>
          <section aria-label="Indicadores principais" className="performance-main-metrics">
            <MetricCard icon={<FaSeedling />} label="Produtividade" value={produtividades.length ? produtividades.join(' · ') : 'Ainda não informada'}
              sub={`${resumo.comResultado} de ${talhoesFiltrados.length} ciclos com resultado; médias por cultura e unidade`} darkMode={darkMode} />
            <MetricCard icon={<FaClipboardList />} label="Gastos registrados" value={custosIndisponiveis ? 'Indisponíveis' : resumo.comCustos ? fmtBRL(resumo.custo) : 'Sem registros'}
              sub={custosIndisponiveis ? 'Atualize os dados antes de analisar' : `${resumo.comCustos} de ${talhoesFiltrados.length} ciclos com gastos`} darkMode={darkMode} />
            <MetricCard icon={<FaRulerCombined />} label="Custo por hectare" value={!custosIndisponiveis && resumo.custoPorHa != null ? fmtBRL(resumo.custoPorHa) : 'Dados insuficientes'}
              sub="Exige área e gastos em todos os ciclos selecionados" darkMode={darkMode} />
            <MetricCard icon={<FaBullseye />} label="Resultado em relação à meta" value={resumo.resultadosComparaveis ? `${resumo.metasAtingidas} de ${resumo.resultadosComparaveis} atingiram` : 'Aguardando dados'}
              sub="Somente resultados com meta definida" darkMode={darkMode} />
          </section>
          <p className="performance-notice">Produção real é informada; meta e cotação são projeções, não vendas nem lucro.</p>
          {(pendencias.length > 0 || (!custosIndisponiveis && conciliacao.unassigned.length > 0)) && <details className="performance-disclosure performance-pending">
            <summary>Pendências para completar a análise</summary>
            <ul>
              {pendencias.map(row => <li key={row._performanceKey}><span>{cicloLabel(row)} — {faltaResultado(row) ? 'falta informar a produção real' : 'meta ainda não definida'}.</span>
                <button type="button" className="crop-performance-report-action" disabled={row._readOnly || salvando || planLoading}
                  onClick={() => abrirEdicao(row, faltaResultado(row))}>{row._readOnly ? 'Somente consulta' : faltaResultado(row) ? 'Informar produção' : 'Definir meta'}</button></li>)}
              {!custosIndisponiveis && conciliacao.unassigned.length > 0 && <li><span>{conciliacao.unassigned.length} gastos da propriedade sem associação a ciclo (todo o histórico).</span>
                <button type="button" className="crop-performance-report-action" disabled={salvando} onClick={conferirGastos}>Conferir gastos</button></li>}
            </ul>
          </details>}

          <details className="performance-disclosure">
            <summary>Ver detalhes e projeções da seleção</summary>
            <div className="performance-grid performance-area-summary">
              <MetricCard label="Área física dos talhões" value={`${fmtNum(resumo.area)} ha`} darkMode={darkMode} />
              <MetricCard label="Área cultivada acumulada" value={`${fmtNum(resumo.areaCultivada)} ha`} darkMode={darkMode} />
            </div>
            {(resumo.comMeta > 0 || resumo.comResultado > 0) && (
            <div className="crop-performance-summary" style={{
              background: cardBg, borderRadius: 16, padding: isMobile ? '16px' : '20px 24px',
              border: `1.5px solid ${border}`, marginBottom: 20,
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            }}>
              <div className="crop-performance-section-heading">
                <div><span>VISÃO CONSOLIDADA</span><h2>Projeção e viabilidade</h2></div>
                <small>Projeções usam meta e cotação. Custos registrados podem estar incompletos; não representam lucro.</small>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? '130px' : '150px'}, 1fr))`, gap: 10 }}>
                <MetricCard icon={<FaRulerCombined />} label="Área com meta" value={`${fmtNum(resumo.areaComMeta)} ha`} sub={`${Math.round(resumo.coberturaMeta)}% da área`} darkMode={darkMode} />
                <MetricCard icon={<FaSeedling />} label="Produção projetada" value={fmtProducao(resumo.producaoPorUnidade)} sub="por cultura e unidade" darkMode={darkMode} />
                {resumo.comResultado > 0 && <MetricCard icon={<FaCheckCircle />} label="Produção real informada" value={fmtProducao(resumo.producaoReal)} sub="não equivale a receita de vendas" darkMode={darkMode} />}
                {resumo.receita > 0 && (
                  <MetricCard icon={<FaWallet />} label="Receita projetada" value={fmtBRL(resumo.receita)} valueColor="#0878a4" sub={`${Math.round(resumo.coberturaFinanceira)}% da área`} darkMode={darkMode} />
                )}
                {resumo.receita > 0 && !custosIndisponiveis && resumo.margem != null && (
                  <MetricCard
                    icon={<FaChartLine />}
                    label="Saldo parcial projetado"
                    value={fmtBRL(resumo.margem)}
                    valueColor={resumo.margem >= 0 ? '#159473' : '#c94b45'}
                    sub={`custos associados: ${fmtBRL(resumo.custoFinanceiro)}; não inclui gastos sem ciclo`}
                    darkMode={darkMode}
                  />
                )}
              </div>
              {!custosIndisponiveis && resumo.receita > 0 && resumo.margem == null && <p className="performance-notice">Saldo não calculado: há ciclos com projeção, mas sem custos registrados. Ausência de lançamentos não comprova custo zero.</p>}
            </div>
          )}

          </details>
          <details className="performance-disclosure">
            <summary>Como os números são calculados</summary>
            <p className="performance-notice">Cada ciclo é identificado por cultura, talhão e data de plantio, sem agrupar safras por aproximação. A data prevista não confirma colheita. Em andamento inclui plantios planejados; concluídas exigem colheita efetiva.</p>
            <p className="performance-notice">Custos só entram no ciclo quando data e talhão correspondem a um único plantio. Gastos gerais, pré/pós-plantio e ambiguidades ficam na visão Geral, sem rateio. Ausência de lançamentos não comprova custo zero.</p>
            <p className="performance-notice">A produtividade média usa a área com resultado informado, separada por cultura e unidade. Na ausência de resultado, mostra a meta identificada como projeção. Resultados parciais não são uma média de todos os ciclos.</p>
            <p className="performance-notice">O histórico usa área, metas, cotações e registros atuais, não um fechamento imutável.</p>
          </details>

          {/* ── Banner de upgrade para starter ──────────────────────────────── */}
          {isStarter && !planLoading && (
            <div
              className="crop-performance-upgrade"
              onClick={() => setShowUpgrade(true)}
              style={{
                background: darkMode ? '#1a1a2e' : '#f3e5f5',
                border: `1.5px solid ${darkMode ? '#4a235a' : '#ce93d8'}`,
                borderRadius: 12, padding: '12px 16px', marginBottom: 8,
                display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: 22 }}><FaLock /></span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: darkMode ? '#ce93d8' : '#7b1fa2' }}>Estimativas bloqueadas no plano Starter</div>
                <div style={{ fontSize: 12, color: darkMode ? '#9ca3af' : '#6b7280', marginTop: 2 }}>Consulte os planos disponíveis para definir metas de produtividade, cotações e margens.</div>
              </div>
              <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: '#9c27b0', whiteSpace: 'nowrap' }}>Ver planos →</span>
            </div>
          )}

          {/* ── Filtro por status ────────────────────────────────────────────── */}
          <div className="crop-performance-filter-row">
          <h2 className="performance-list-title">Ciclos por talhão <small>({talhoesFiltrados.length})</small></h2>
          <div className="performance-export-actions" role="group" aria-label="Exportar ciclos agrícolas">
            <button className="crop-performance-report-action" onClick={() => exportarRelatorio('xlsx')} disabled={gerandoRelatorio || planLoading}>
              <FaFileExcel aria-hidden="true" /> {gerandoRelatorio ? 'Gerando…' : 'Exportar XLSX'}
            </button>
            <button className="crop-performance-report-action" onClick={() => exportarRelatorio('pdf')} disabled={gerandoRelatorio || planLoading}>
              <FaFilePdf aria-hidden="true" /> {gerandoRelatorio ? 'Gerando…' : 'Exportar PDF'}
            </button>
          </div>
          </div>

          {/* ── Cards por talhão ──────────────────────────────────────────────── */}
          {talhoesFiltrados.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: textSecondary }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>🔍</div>
              Nenhum talhão encontrado neste filtro.
              {filtroStatus !== 'todos' && <button type="button" className="crop-performance-report-action" onClick={() => setFiltroStatus('todos')}>Ver todas as safras</button>}
            </div>
          ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {talhoesFiltrados.map(t => {
              const est = estimativas[t._performanceKey];
              const { area, cost: custo, production: prod, revenue: receita, margin: margem, breakEven: custoUnid, realProduction: producaoReal } = cycleMetrics(t, est, custosIndisponiveis ? {} : custos);
              const unidade = est?.unidade || UNIDADE_CULTURA[t.cultura] || 'sc';
              const distanciaEquilibrio = custoUnid > 0 && est?.cotacao
                ? ((parseFloat(est.cotacao) - custoUnid) / custoUnid) * 100
                : null;
              const desvioMeta = hasValue(est?.real) && Number(est?.produtividade) > 0
                ? ((parseFloat(est.real) - parseFloat(est.produtividade)) / parseFloat(est.produtividade)) * 100
                : null;
              const colhido = isColhido(t);
              const planejado = isPlanejado(t);
              const readOnly = t._readOnly;
              const isEditing = editandoId === t._performanceKey;

              return (
                <article className="crop-performance-plot" aria-label={cicloLabel(t)} key={t._performanceKey} style={{
                  background: cardBg, borderRadius: 14,
                  border: `1.5px solid ${border}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)', overflow: 'hidden',
                }}>
                  {/* ── Cabeçalho do card ──────────────────────────────────────── */}
                  <div className="crop-performance-plot-header" style={{
                    padding: isMobile ? '12px 14px' : '14px 18px',
                    background: colhido
                      ? (darkMode ? '#0f1f10' : '#f1f8e9')
                      : planejado
                        ? (darkMode ? '#2b2110' : '#fff7e4')
                        : (darkMode ? '#0d1628' : '#e8f4fd'),
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10,
                  }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, color: textPrimary }}>{t.nome}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginTop: 4 }}>
                        <span style={{ fontSize: 12, color: textSecondary }}><FaSeedling /> {t.cultura}</span>
                        <span className="crop-performance-season-badge">Plantio {t.data_plantio.split('-').reverse().join('/')}</span>
                        {t.area && <span style={{ fontSize: 12, color: textSecondary }}><FaRulerCombined /> {t.area} ha</span>}
                        {colhido && (
                          <span style={{ fontSize: 12, color: 'var(--crop-green)', fontWeight: 600 }}><FaCheckCircle /> Concluída</span>
                        )}
                        {planejado && (
                          <span style={{ fontSize: 12, color: '#b56f08', fontWeight: 700 }}>🗓️ Pré-plantio</span>
                        )}
                        {!colhido && !planejado && <span style={{ fontSize: 12, color: textSecondary }}>Em andamento</span>}
                      </div>
                    </div>
                    {!isEditing && (
                      <button
                        className="crop-performance-edit-action"
                        onClick={() => isStarter ? setShowUpgrade(true) : abrirEdicao(t, faltaResultado(t))}
                        disabled={readOnly || salvando || planLoading}
                        title={readOnly ? 'Plantio físico sem vínculo para gravar metas; disponível para consulta.' : undefined}
                        style={{
                          flexShrink: 0,
                          background: readOnly
                            ? (darkMode ? '#202938' : '#edf2f5')
                            : isStarter
                            ? (darkMode ? '#2a2a2a' : '#f5f5f5')
                            : est ? (darkMode ? '#1e3a1e' : '#e8f5e9') : 'linear-gradient(135deg, #43a047, #2e7d32)',
                          color: readOnly ? textSecondary : isStarter ? (darkMode ? '#9ca3af' : '#9e9e9e') : est ? '#43a047' : '#fff',
                          border: readOnly ? `1.5px solid ${border}` : isStarter ? `1.5px solid ${darkMode ? '#374151' : '#e0e0e0'}` : est ? '1.5px solid #43a047' : 'none',
                          borderRadius: 8,
                          padding: isMobile ? '12px 16px' : '6px 14px', minHeight: 44,
                          fontWeight: 600, fontSize: 12, cursor: readOnly ? 'not-allowed' : 'pointer',
                          WebkitTapHighlightColor: 'transparent',
                        }}
                      >
                        {readOnly ? 'Somente consulta' : isStarter ? <><FaLock /> Pro</> : faltaResultado(t) ? 'Informar produção' : est ? <><FaPen /> Editar</> : <><FaBullseye /> Definir meta</>}
                      </button>
                    )}
                  </div>
                  {!isEditing && <dl className="performance-cycle-summary">
                    <div><dt>{hasValue(est?.real) ? 'Produtividade real' : 'Meta de produtividade'}</dt>
                      <dd>{hasValue(est?.real) ? `${fmtNum(est.real)} ${unidade}/ha` : Number(est?.produtividade) > 0 ? `${fmtNum(est.produtividade)} ${unidade}/ha` : 'Não informada'}</dd></div>
                    <div><dt>Gastos registrados</dt><dd>{custosIndisponiveis ? 'Indisponíveis' : custo != null ? fmtBRL(custo) : 'Sem registros'}</dd></div>
                  </dl>}

                  {/* ── Formulário de edição ───────────────────────────────────── */}
                  {isEditing && (
                    <div ref={editorRef} className="crop-performance-editor" style={{
                      padding: isMobile ? '14px' : '16px 18px',
                      borderBottom: `1px solid ${border}`,
                      background: darkMode ? '#0a1525' : '#f8fbff',
                    }}>
                      <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? '150px' : '180px'}, 1fr))`, gap: 12, marginBottom: 14 }}>
                        <div>
                          <label style={{ fontSize: 12, color: textSecondary, display: 'block', marginBottom: 5 }}>
                            Produtividade estimada ({unidade}/ha)
                          </label>
                          <input
                            aria-label="Produtividade estimada"
                            type="number" step="0.1" min="0"
                            value={form.produtividade}
                            onChange={e => setForm(f => ({ ...f, produtividade: e.target.value }))}
                            placeholder={PROD_TIPICA[t.cultura] ? `Ex: ${PROD_TIPICA[t.cultura]}` : 'ex: 60'}
                            style={inputStyle}
                          />
                          {PROD_TIPICA[t.cultura] && (
                            <div style={{ fontSize: 10, color: textSecondary, marginTop: 4 }}>
                              Média nacional: ~{PROD_TIPICA[t.cultura]} {unidade}/ha
                            </div>
                          )}
                        </div>
                        <div>
                          <label style={{ fontSize: 12, color: textSecondary, display: 'block', marginBottom: 5 }}>
                            Cotação (R$/{unidade})
                          </label>
                          <input
                            aria-label="Cotação unitária"
                            type="number" step="0.01" min="0"
                            value={form.cotacao}
                            onChange={e => setForm(f => ({ ...f, cotacao: e.target.value }))}
                            placeholder={COTACOES_SUGERIDAS[t.cultura] ? `Ex: ${COTACOES_SUGERIDAS[t.cultura]}` : 'R$'}
                            style={inputStyle}
                          />
                          {COTACOES_SUGERIDAS[t.cultura] && (
                            <div style={{ fontSize: 10, color: textSecondary, marginTop: 4 }}>
                              Sugestão: R$ {COTACOES_SUGERIDAS[t.cultura]}/{unidade}
                            </div>
                          )}
                        </div>
                        {colhido && (
                          <div>
                            <label style={{ fontSize: 12, color: '#43a047', fontWeight: 600, display: 'block', marginBottom: 5 }}>
                              ✅ Produtividade real ({unidade}/ha)
                            </label>
                            <input
                              aria-label="Produtividade real"
                              type="number" step="0.1" min="0"
                              value={form.real}
                              onChange={e => setForm(f => ({ ...f, real: e.target.value }))}
                              placeholder="Resultado obtido"
                              style={{ ...inputStyle, borderColor: '#43a047' }}
                            />
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => salvar(t)}
                          disabled={salvando}
                          style={{
                            background: salvando ? '#9e9e9e' : 'linear-gradient(135deg, #43a047, #2e7d32)',
                            color: '#fff', border: 'none', borderRadius: 8,
                            padding: isMobile ? '13px 22px' : '9px 22px',
                            minHeight: 44, fontWeight: 700,
                            cursor: salvando ? 'not-allowed' : 'pointer', fontSize: 13,
                            WebkitTapHighlightColor: 'transparent',
                          }}
                        >
                          {salvando ? 'Salvando…' : <><FaCheck /> Salvar</>}
                        </button>
                        <button
                          onClick={() => setEditandoId(null)}
                          disabled={salvando}
                          style={{
                            background: 'transparent', color: textSecondary,
                            border: `1.5px solid ${border}`, borderRadius: 8,
                            padding: isMobile ? '13px 16px' : '9px 16px',
                            minHeight: 44, fontWeight: 600, cursor: 'pointer', fontSize: 13,
                            WebkitTapHighlightColor: 'transparent',
                          }}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}

                  <details className="performance-disclosure performance-cycle-details">
                    <summary>Ver detalhes do ciclo</summary>
                    <p className="performance-notice">Colheita efetiva: {t.data_colheita_efetiva ? t.data_colheita_efetiva.split('-').reverse().join('/') : 'Não registrada'}</p>
                  {est && (est.produtividade || est.cotacao || hasValue(est.real) || custo != null) && !isEditing && (
                    <div style={{ padding: isMobile ? '12px 14px' : '14px 18px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? '130px' : '150px'}, 1fr))`, gap: 10 }}>
                        {est.produtividade && (
                          <MetricCard icon={<FaBullseye />} label={`Meta (${unidade}/ha)`} value={`${fmtNum(est.produtividade)} ${unidade}/ha`} valueColor="#0878a4" darkMode={darkMode} />
                        )}
                        {prod != null && (
                          <MetricCard icon={<FaSeedling />} label={`Produção est. (${unidade})`} value={`${fmtNum(prod, 0)} ${unidade}`} darkMode={darkMode} />
                        )}
                        {receita != null && (
                          <MetricCard icon={<FaCoins />} label="Receita estimada" value={fmtBRL(receita)} valueColor="#0878a4" darkMode={darkMode} />
                        )}
                        {custo != null && (
                          <MetricCard icon={<FaClipboardList />} label="Custo registrado" value={fmtBRL(custo)} valueColor="#d88916" sub="caderno e eventos; associado por data" darkMode={darkMode} />
                        )}
                        {custo != null && area > 0 && <MetricCard icon={<FaRulerCombined />} label="Custo por hectare" value={fmtBRL(custo / area)} sub="gastos associados ao ciclo" darkMode={darkMode} />}
                        {producaoReal != null && <MetricCard icon={<FaCheckCircle />} label="Produção real" value={`${fmtNum(producaoReal)} ${unidade}`} sub="informada pelo usuário; não é venda" darkMode={darkMode} />}
                        {producaoReal > 0 && custo != null && <MetricCard icon={<FaCoins />} label={`Custo por ${unidade} produzida`} value={fmtBRL(custo / producaoReal)} sub="baseado na produção real" darkMode={darkMode} />}
                        {margem != null && (
                          <MetricCard
                            icon={<FaChartLine />}
                            label="Saldo parcial projetado"
                            value={fmtBRL(margem)}
                            valueColor={margem >= 0 ? '#2e7d32' : '#c62828'}
                            sub="meta × cotação menos gastos associados"
                            darkMode={darkMode}
                          />
                        )}
                        {custoUnid != null && (
                          <MetricCard
                            icon={<FaBalanceScale />}
                            label={`Break-even (R$/${unidade})`}
                            value={`R$ ${fmtNum(custoUnid, 2)}/${unidade}`}
                            sub="pela meta; apenas custos registrados"
                            darkMode={darkMode}
                          />
                        )}
                        {hasValue(est.real) && (
                          <MetricCard
                            icon={Number(est.produtividade) > 0 ? (Number(est.real) >= Number(est.produtividade) ? '✅' : '⚠️') : <FaCheckCircle />}
                            label={`Prod. real (${unidade}/ha)`}
                            value={`${fmtNum(est.real)} ${unidade}/ha`}
                            valueColor={Number(est.produtividade) > 0 ? (Number(est.real) >= Number(est.produtividade) ? '#2e7d32' : '#e65100') : undefined}
                            sub={Number(est.produtividade) > 0
                              ? `meta: ${fmtNum(est.produtividade)} — ${parseFloat(est.real) >= parseFloat(est.produtividade) ? '✓ atingida' : '✗ abaixo'}`
                              : undefined}
                            darkMode={darkMode}
                          />
                        )}
                      </div>
                      {(distanciaEquilibrio != null || desvioMeta != null) && (
                        <div className={`crop-performance-plot-insight ${(desvioMeta != null ? desvioMeta : distanciaEquilibrio) >= 0 ? 'is-positive' : 'is-attention'}`}>
                          <FaChartLine />
                          <span>
                            {desvioMeta != null
                              ? `Resultado real ${Math.abs(desvioMeta).toFixed(1)}% ${desvioMeta >= 0 ? 'acima' : 'abaixo'} da meta.`
                              : `Cotação ${Math.abs(distanciaEquilibrio).toFixed(1)}% ${distanciaEquilibrio >= 0 ? 'acima' : 'abaixo'} do ponto de equilíbrio registrado.`}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Sem dados ainda ───────────────────────────────────────── */}
                  {!est && !isEditing && custo == null && !readOnly && (
                    <div style={{ padding: '12px 18px', color: textSecondary, fontSize: 13 }}>
                      Nenhuma estimativa cadastrada. Use a ação do ciclo para informar produtividade ou definir uma meta.
                    </div>
                  )}
                  {readOnly && (
                    <div style={{ padding: '12px 18px', color: textSecondary, fontSize: 13 }}>
                      Plantio físico disponível para consulta. A gravação de metas depende de um vínculo persistido com o cadastro de produtividade; nenhum vínculo será criado por aproximação.
                    </div>
                  )}
                  {t._legacy && <p className="performance-notice">Cadastro legado sem ciclo confirmado. Metas antigas foram preservadas; os gastos permanecem na visão Geral até haver um ciclo compatível.</p>}
                  {!est && !isEditing && custo != null && (
                    <div style={{ padding: isMobile ? '12px 14px' : '12px 18px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? '130px' : '150px'}, 1fr))`, gap: 10, marginBottom: 10 }}>
                        <MetricCard icon={<FaClipboardList />} label="Custo registrado" value={fmtBRL(custo)} valueColor="#d88916" sub="caderno e eventos; associado por data" darkMode={darkMode} />
                      </div>
                      {!readOnly && <div style={{ fontSize: 12, color: textSecondary }}>Defina a meta de produtividade para calcular a projeção.</div>}
                    </div>
                  )}
                  </details>
                </article>
              );
            })}
          </div>
          )}
          </>}
        </>
      )}

      </>}
      <UpgradeModal
        show={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        recurso="produtividade"
        planoAtual={plano?.tipo || 'starter'}
        usuario={user}
      />
    </div>
  );
}
